import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { formatDualCurrency } from "../config/fx";
import { 
  FileText,
  Package,
  Plus,
  Eye,
  Clock,
  CheckCircle2,
  MapPin,
  Building2,
  Send
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";
const destinations = ['Kenya', 'Uganda', 'Tanzania', 'Ethiopia', 'Rwanda'];

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, isBuyer, isSupplier } = useAuth();
  
  const [myRfqs, setMyRfqs] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [supplierProfile, setSupplierProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [showEditRfqModal, setShowEditRfqModal] = useState(false);
  const [editRfq, setEditRfq] = useState(null);
  const [editRfqForm, setEditRfqForm] = useState({
    category: '',
    specs: '',
    quantity: '',
    destination: ''
  });
  const [showEditBidModal, setShowEditBidModal] = useState(false);
  const [editBid, setEditBid] = useState(null);
  const [editBidForm, setEditBidForm] = useState({
    price_usd: '',
    lead_time_days: '',
    message: ''
  });
  const [showBargainModal, setShowBargainModal] = useState(false);
  const [activeBargainBid, setActiveBargainBid] = useState(null);
  const [bargainHistory, setBargainHistory] = useState([]);
  const [bargainLoading, setBargainLoading] = useState(false);
  const [bargainForm, setBargainForm] = useState({
    price_usd: '',
    lead_time_days: '',
    message: ''
  });

  // Product form
  // Product form
const [showProductModal, setShowProductModal] = useState(false);
const [productForm, setProductForm] = useState({
  name: '',
  category: '',
  price_min_usd: '',
  price_max_usd: '',
  moq: '',
  lead_time_days: '',
  specs: {}
});

// ✅ Image state
const [productImage, setProductImage] = useState(null);
const [imagePreview, setImagePreview] = useState(null);

// Edit product state
const [showEditProductModal, setShowEditProductModal] = useState(false);
const [editProduct, setEditProduct] = useState(null);
const [editProductForm, setEditProductForm] = useState({
  name: '',
  category: '',
  price_min_usd: '',
  price_max_usd: '',
  moq: '',
  lead_time_days: '',
  clear_image: false
});
const [editProductImage, setEditProductImage] = useState(null);
const [editImagePreview, setEditImagePreview] = useState(null);

// ✅ Image picker (loads from user's local computer)
const handleImageChange = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  // optional: simple file type guard
  const okTypes = ["image/jpeg", "image/png", "image/webp"];
  if (!okTypes.includes(file.type)) {
    alert("Please select a JPG, PNG, or WEBP image");
    return;
  }

  setProductImage(file);

  // clean old preview to avoid memory leaks
  if (imagePreview) URL.revokeObjectURL(imagePreview);

  setImagePreview(URL.createObjectURL(file));
};

// ✅ MUST EXIST because your UI button calls it
const removeImage = () => {
  setProductImage(null);
  if (imagePreview) URL.revokeObjectURL(imagePreview);
  setImagePreview(null);

  // optional: also clear the file input if you use a ref
};

const handleEditImageChange = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  const okTypes = ["image/jpeg", "image/png", "image/webp"];
  if (!okTypes.includes(file.type)) {
    alert("Please select a JPG, PNG, or WEBP image");
    return;
  }

  setEditProductImage(file);
  setEditProductForm((prev) => ({ ...prev, clear_image: false }));

  if (editImagePreview && editImagePreview.startsWith("blob:")) {
    URL.revokeObjectURL(editImagePreview);
  }

  setEditImagePreview(URL.createObjectURL(file));
};

const removeEditImage = () => {
  setEditProductImage(null);
  if (editImagePreview && editImagePreview.startsWith("blob:")) {
    URL.revokeObjectURL(editImagePreview);
  }
  setEditImagePreview(null);
};

const clearEditImage = () => {
  setEditProductImage(null);
  if (editImagePreview && editImagePreview.startsWith("blob:")) {
    URL.revokeObjectURL(editImagePreview);
  }
  setEditImagePreview(null);
  setEditProductForm((prev) => ({ ...prev, clear_image: true }));
};

  // Supplier form
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierForm, setSupplierForm] = useState({
    company_name: '',
    location: '',
    categories: [],
    description: ''
  });
  const [showEditSupplierModal, setShowEditSupplierModal] = useState(false);
  const [editSupplierForm, setEditSupplierForm] = useState({
    company_name: '',
    location: '',
    categories: [],
    description: ''
  });

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, [user]);

  const getErrorMessage = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const messages = detail.map((item) => item?.msg).filter(Boolean);
      return messages.length > 0 ? messages.join(", ") : fallback;
    }
    return fallback;
  };

  const getImageUrl = (paths) => {
    if (!paths || paths.length === 0) return null;
    const path = paths[0];
    if (!path) return null;
    return path.startsWith("http") ? path : `${API_URL}${path}`;
  };

  const formatBargainTimestamp = (bargain) => {
    const raw = bargain.created_at || bargain.createdAt || bargain.timestamp;
    if (!raw) return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return date.toLocaleString();
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (isBuyer || user?.role === 'admin') {
        const rfqRes = await axios.get(`${API_URL}/api/rfqs/my/list`);
        setMyRfqs(rfqRes.data);
      }
      
      if (isSupplier || user?.role === 'admin') {
        try {
          const profileRes = await axios.get(`${API_URL}/api/suppliers/my/profile`);
          setSupplierProfile(profileRes.data);
        } catch (e) {
          // No supplier profile yet
        }
        
        const productsRes = await axios.get(`${API_URL}/api/products/my/list`);
        setMyProducts(productsRes.data);
        
        const bidsRes = await axios.get(`${API_URL}/api/bids/my/list`);
        setMyBids(bidsRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.categories);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const handleCreateSupplierProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/suppliers`, supplierForm);
      toast.success('Supplier profile created');
      setShowSupplierModal(false);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create profile'));
    }
  };

  const openEditSupplierModal = () => {
    if (!supplierProfile) return;
    setEditSupplierForm({
      company_name: supplierProfile.company_name || '',
      location: supplierProfile.location || '',
      categories: supplierProfile.categories || [],
      description: supplierProfile.description || ''
    });
    setShowEditSupplierModal(true);
  };

  const handleUpdateSupplierProfile = async (e) => {
    e.preventDefault();
    if (!supplierProfile) return;
    try {
      await axios.put(`${API_URL}/api/suppliers/${supplierProfile.id}`, editSupplierForm);
      toast.success('Supplier profile updated');
      setShowEditSupplierModal(false);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update profile'));
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("name", productForm.name);
      formData.append("category", productForm.category);
      formData.append("price_min_usd", productForm.price_min_usd);
      formData.append("price_max_usd", productForm.price_max_usd);
      formData.append("moq", productForm.moq);
      formData.append("lead_time_days", productForm.lead_time_days);

      if (productImage) {
        formData.append("image", productImage);
      }

      await axios.post(`${API_URL}/api/products`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success('Product created');
      setShowProductModal(false);
      setProductForm({ name: '', category: '', price_min_usd: '', price_max_usd: '', moq: '', lead_time_days: '', specs: {} });
      removeImage();
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create product'));
    }
  };

const openEditProductModal = (product) => {
  if (!product) return;
  setEditProduct(product);
  setEditProductForm({
    name: product.name || '',
    category: product.category || '',
    price_min_usd: product.price_min_usd?.toString() || '',
    price_max_usd: product.price_max_usd?.toString() || '',
    moq: product.moq?.toString() || '',
    lead_time_days: product.lead_time_days?.toString() || '',
    clear_image: false
  });
    setEditProductImage(null);
    if (editImagePreview && editImagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(editImagePreview);
    }
    setEditImagePreview(getImageUrl(product.image_paths));
    setShowEditProductModal(true);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!editProduct) return;
    try {
      const formData = new FormData();
      formData.append("name", editProductForm.name);
      formData.append("category", editProductForm.category);
      formData.append("price_min_usd", editProductForm.price_min_usd);
      formData.append("price_max_usd", editProductForm.price_max_usd);
      formData.append("moq", editProductForm.moq);
      formData.append("lead_time_days", editProductForm.lead_time_days);
      if (editProductForm.clear_image) {
        formData.append("clear_image", "true");
      }

      if (editProductImage) {
        formData.append("image", editProductImage);
      }

      await axios.put(`${API_URL}/api/products/${editProduct.id}/form`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success('Product updated');
      setShowEditProductModal(false);
      setEditProduct(null);
      removeEditImage();
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update product'));
    }
  };

  const openEditRfqModal = (rfq) => {
    if (!rfq) return;
    setEditRfq(rfq);
    setEditRfqForm({
      category: rfq.category || '',
      specs: rfq.specs || '',
      quantity: rfq.quantity?.toString() || '',
      destination: rfq.destination || ''
    });
    setShowEditRfqModal(true);
  };

  const handleUpdateRfq = async (e) => {
    e.preventDefault();
    if (!editRfq) return;
    try {
      await axios.put(`${API_URL}/api/rfqs/${editRfq.id}`, {
        category: editRfqForm.category,
        specs: editRfqForm.specs,
        quantity: parseInt(editRfqForm.quantity),
        destination: editRfqForm.destination
      });
      toast.success('RFQ updated');
      setShowEditRfqModal(false);
      setEditRfq(null);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update RFQ'));
    }
  };

  const openEditBidModal = (bid) => {
    if (!bid) return;
    setEditBid(bid);
    setEditBidForm({
      price_usd: bid.price_usd?.toString() || '',
      lead_time_days: bid.lead_time_days?.toString() || '',
      message: bid.message || ''
    });
    setShowEditBidModal(true);
  };

  const handleUpdateBid = async (e) => {
    e.preventDefault();
    if (!editBid) return;
    try {
      await axios.put(`${API_URL}/api/bids/${editBid.id}`, {
        price_usd: parseFloat(editBidForm.price_usd),
        lead_time_days: parseInt(editBidForm.lead_time_days),
        message: editBidForm.message
      });
      toast.success('Bid updated');
      setShowEditBidModal(false);
      setEditBid(null);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update bid'));
    }
  };

  const fetchBargainHistory = async (bidId) => {
    try {
      const response = await axios.get(`${API_URL}/api/bids/${bidId}/bargain`);
      setBargainHistory(response.data || []);
    } catch (err) {
      console.error('Failed to fetch bargain history:', err);
    }
  };

  const openBargainModal = (bid) => {
    setActiveBargainBid(bid);
    setBargainForm({ price_usd: '', lead_time_days: '', message: '' });
    setShowBargainModal(true);
    fetchBargainHistory(bid.id);
  };

  useEffect(() => {
    if (!showBargainModal || !activeBargainBid) return;
    const intervalId = setInterval(() => {
      fetchBargainHistory(activeBargainBid.id);
    }, 5000);
    return () => clearInterval(intervalId);
  }, [showBargainModal, activeBargainBid]);

  const handleSubmitBargain = async () => {
    if (!activeBargainBid) return;
    if (!bargainForm.price_usd || !bargainForm.lead_time_days) return;
    setBargainLoading(true);
    try {
      await axios.post(`${API_URL}/api/bids/${activeBargainBid.id}/bargain`, {
        price_usd: parseFloat(bargainForm.price_usd),
        lead_time_days: parseInt(bargainForm.lead_time_days),
        message: bargainForm.message
      });
      toast.success('Bargain sent');
      setBargainForm({ price_usd: '', lead_time_days: '', message: '' });
      fetchBargainHistory(activeBargainBid.id);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send bargain'));
    }
    setBargainLoading(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-[#10B981]',
      closed: 'bg-slate-500',
      awarded: 'bg-[#D4AF37]',
      pending: 'bg-yellow-500',
      accepted: 'bg-[#10B981]',
      rejected: 'bg-red-500'
    };
    return <Badge className={styles[status]}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D4F8B]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="dashboard-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container-main py-8">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('dashboard.title')}
          </h1>
          <p className="text-slate-500">
            {t('dashboard.welcome')}, {user?.name}
          </p>
        </div>
      </div>

      <div className="container-main py-8">
        {/* Buyer Dashboard */}
        {(isBuyer || user?.role === 'admin') && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('dashboard.buyer.rfqs')}
              </h2>
              <Link to="/rfq">
                <Button className="bg-[#0D4F8B] hover:bg-[#0A3D6B]">
                  <Plus className="w-4 h-4 mr-2" />
                  Create RFQ (Request for Quotation)
                </Button>
              </Link>
            </div>

            {myRfqs.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No RFQs yet</p>
                  <Link to="/rfq" className="inline-block mt-4">
                    <Button variant="outline">Create your first RFQ</Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myRfqs.slice(0, 6).map((rfq) => (
                  <Card key={rfq.id} className="card-base" data-testid={`dashboard-rfq-${rfq.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="outline">{rfq.category}</Badge>
                        {getStatusBadge(rfq.status)}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{rfq.specs}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>Qty: {rfq.quantity}</span>
                        <span>{rfq.destination}</span>
                      </div>
                      {rfq.status === 'open' && (
                        <div className="mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditRfqModal(rfq)}
                          >
                            Edit
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Supplier Dashboard */}
        {(isSupplier || user?.role === 'admin') && (
          <div>
            {/* Supplier Profile */}
            {!supplierProfile ? (
              <Card className="card-base mb-8">
                <CardContent className="p-8 text-center">
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Create Your Supplier Profile</h3>
                  <p className="text-slate-500 mb-4">Set up your company profile to start listing products</p>
                  <Dialog open={showSupplierModal} onOpenChange={setShowSupplierModal}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#10B981] hover:bg-[#059669]">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Profile
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create Supplier Profile</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateSupplierProfile} className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">Company Name *</label>
                          <Input
                            value={supplierForm.company_name}
                            onChange={(e) => setSupplierForm({ ...supplierForm, company_name: e.target.value })}
                            placeholder="Your company name"
                            aria-label="Company name"
                            data-testid="supplier-company-name"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Location *</label>
                          <Input
                            value={supplierForm.location}
                            onChange={(e) => setSupplierForm({ ...supplierForm, location: e.target.value })}
                            placeholder="e.g., Dubai, UAE"
                            aria-label="Company location"
                            data-testid="supplier-location"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Categories *</label>
                          <Select
                            value={supplierForm.categories[0] || ''}
                            onValueChange={(v) => setSupplierForm({ ...supplierForm, categories: [v] })}
                          >
                            <SelectTrigger aria-label="Supplier category">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((cat) => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm font-medium">Description *</label>
                          <Textarea
                            value={supplierForm.description}
                            onChange={(e) => setSupplierForm({ ...supplierForm, description: e.target.value })}
                            placeholder="Describe your company..."
                            aria-label="Supplier description"
                            rows={3}
                          />
                        </div>
                        <Button type="submit" className="w-full bg-[#10B981] hover:bg-[#059669]">
                          Create Profile
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            ) : (
              <Card className="card-base mb-8">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-[#0D4F8B] rounded-md flex items-center justify-center text-white font-bold text-2xl">
                        {supplierProfile.company_name.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-bold text-slate-900">{supplierProfile.company_name}</h3>
                          {supplierProfile.verified && (
                            <Badge className="bg-[#10B981]">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Verified
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <MapPin className="w-4 h-4" />
                          {supplierProfile.location}
                        </div>
                      </div>
                    </div>
                    <Dialog open={showEditSupplierModal} onOpenChange={setShowEditSupplierModal}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" onClick={openEditSupplierModal}>
                          Edit Profile
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Edit Supplier Profile</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleUpdateSupplierProfile} className="space-y-4">
                          <div>
                            <label className="text-sm font-medium">Company Name *</label>
                          <Input
                            value={editSupplierForm.company_name}
                            onChange={(e) => setEditSupplierForm({ ...editSupplierForm, company_name: e.target.value })}
                            placeholder="Your company name"
                            aria-label="Edit company name"
                            data-testid="edit-supplier-company-name"
                          />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Location *</label>
                          <Input
                            value={editSupplierForm.location}
                            onChange={(e) => setEditSupplierForm({ ...editSupplierForm, location: e.target.value })}
                            placeholder="e.g., Dubai, UAE"
                            aria-label="Edit company location"
                            data-testid="edit-supplier-location"
                          />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Categories *</label>
                          <Select
                            value={editSupplierForm.categories[0] || ''}
                            onValueChange={(v) => setEditSupplierForm({ ...editSupplierForm, categories: [v] })}
                          >
                            <SelectTrigger aria-label="Edit supplier category">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                              <SelectContent>
                                {categories.map((cat) => (
                                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-sm font-medium">Description *</label>
                          <Textarea
                            value={editSupplierForm.description}
                            onChange={(e) => setEditSupplierForm({ ...editSupplierForm, description: e.target.value })}
                            placeholder="Describe your company..."
                            aria-label="Edit supplier description"
                            rows={3}
                          />
                          </div>
                          <Button type="submit" className="w-full bg-[#10B981] hover:bg-[#059669]">
                            Save Changes
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Products */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t('dashboard.supplier.products')}
                </h2>
                <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#0D4F8B] hover:bg-[#0A3D6B]" disabled={!supplierProfile}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Add New Product</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateProduct} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">Product Name *</label>
                        <Input
                          value={productForm.name}
                          onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                          placeholder="Product name"
                          data-testid="product-name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Category *</label>
                          <Select
                            value={productForm.category}
                            onValueChange={(v) => setProductForm({ ...productForm, category: v })}
                          >
                          <SelectTrigger data-testid="product-category" aria-label="Product category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Min Price (USD) *</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={productForm.price_min_usd}
                            onChange={(e) => setProductForm({ ...productForm, price_min_usd: e.target.value })}
                            placeholder="0.00"
                            data-testid="product-min-price"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Max Price (USD) *</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={productForm.price_max_usd}
                            onChange={(e) => setProductForm({ ...productForm, price_max_usd: e.target.value })}
                            placeholder="0.00"
                            data-testid="product-max-price"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">MOQ (Minimum Order Quantity) *</label>
                          <Input
                            type="number"
                            value={productForm.moq}
                            onChange={(e) => setProductForm({ ...productForm, moq: e.target.value })}
                            placeholder="100"
                            data-testid="product-moq"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Lead Time (days) *</label>
                          <Input
                            type="number"
                            value={productForm.lead_time_days}
                            onChange={(e) => setProductForm({ ...productForm, lead_time_days: e.target.value })}
                            placeholder="7"
                            data-testid="product-leadtime"
                          />
                        </div>

                      </div>
                      {/* Product Image Upload */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Product Image</label>

                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="h-11 w-full cursor-pointer rounded-md border border-slate-200 bg-white text-sm text-slate-600 file:mr-3 file:h-9 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0D4F8B]/20"
                            aria-label="Upload product image"
                            data-testid="product-image"
                          />

                        {imagePreview && (
                          <div className="mt-3 space-y-2">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="h-40 w-full rounded-lg object-contain border"
                            />

                            <Button
                              type="button"
                              variant="outline"
                              onClick={removeImage}
                              className="h-10"
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </div>
                      <Button type="submit" className="w-full bg-[#10B981] hover:bg-[#059669]" data-testid="create-product-btn">
                        Create Product
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
                <Dialog open={showEditProductModal} onOpenChange={setShowEditProductModal}>
                  <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Edit Product</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUpdateProduct} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">Product Name *</label>
                        <Input
                          value={editProductForm.name}
                          onChange={(e) => setEditProductForm({ ...editProductForm, name: e.target.value })}
                          placeholder="Product name"
                          data-testid="edit-product-name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Category *</label>
                        <Select
                          value={editProductForm.category}
                          onValueChange={(v) => setEditProductForm({ ...editProductForm, category: v })}
                        >
                          <SelectTrigger data-testid="edit-product-category" aria-label="Edit product category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Min Price (USD) *</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={editProductForm.price_min_usd}
                            onChange={(e) => setEditProductForm({ ...editProductForm, price_min_usd: e.target.value })}
                            placeholder="0.00"
                            data-testid="edit-product-min-price"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Max Price (USD) *</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={editProductForm.price_max_usd}
                            onChange={(e) => setEditProductForm({ ...editProductForm, price_max_usd: e.target.value })}
                            placeholder="0.00"
                            data-testid="edit-product-max-price"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">MOQ (Minimum Order Quantity) *</label>
                          <Input
                            type="number"
                            value={editProductForm.moq}
                            onChange={(e) => setEditProductForm({ ...editProductForm, moq: e.target.value })}
                            placeholder="100"
                            data-testid="edit-product-moq"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Lead Time (days) *</label>
                          <Input
                            type="number"
                            value={editProductForm.lead_time_days}
                            onChange={(e) => setEditProductForm({ ...editProductForm, lead_time_days: e.target.value })}
                            placeholder="7"
                            data-testid="edit-product-leadtime"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Product Image</label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleEditImageChange}
                          className="h-11 w-full cursor-pointer rounded-md border border-slate-200 bg-white text-sm text-slate-600 file:mr-3 file:h-9 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0D4F8B]/20"
                          aria-label="Upload updated product image"
                          data-testid="edit-product-image"
                        />
                        {editImagePreview && (
                          <div className="mt-3 space-y-2">
                            <img
                              src={editImagePreview}
                              alt="Preview"
                              className="h-40 w-full rounded-lg object-contain border"
                            />
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={removeEditImage}
                                className="h-10"
                              >
                                Remove
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={clearEditImage}
                                className="h-10"
                              >
                                Clear Image
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                      <Button type="submit" className="w-full bg-[#10B981] hover:bg-[#059669]" data-testid="update-product-btn">
                        Save Changes
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {myProducts.length === 0 ? (
                <Card className="card-base">
                  <CardContent className="p-8 text-center">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No products yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myProducts.map((product) => (
                    <Card key={product.id} className="card-base" data-testid={`dashboard-product-${product.id}`}>
                      <CardContent className="p-4">
                        {getImageUrl(product.image_paths) ? (
                          <img
                            src={getImageUrl(product.image_paths)}
                            alt={product.name}
                            className="h-32 w-full rounded-md object-cover mb-3"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-32 w-full rounded-md bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mb-3">
                            <Package className="w-8 h-8 text-slate-300" />
                          </div>
                        )}
                        <Badge variant="outline" className="mb-2">{product.category}</Badge>
                        <h3 className="font-semibold text-slate-900 mb-2">{product.name}</h3>
                        <div className="text-lg font-bold text-[#0D4F8B] mb-2">
                          {formatDualCurrency(product.price_min_usd).usd}
                        </div>
                        <div className="text-xs text-slate-500 mb-2">
                          {formatDualCurrency(product.price_min_usd).aed}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>MOQ: {product.moq}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {product.lead_time_days}d
                          </span>
                        </div>
                        <div className="mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditProductModal(product)}
                          >
                            Edit
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* My Bids */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('dashboard.supplier.bids')}
              </h2>
              {myBids.length === 0 ? (
                <Card className="card-base">
                  <CardContent className="p-8 text-center">
                    <Send className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No bids submitted yet</p>
                    <Link to="/rfq" className="inline-block mt-4">
                      <Button variant="outline">Browse RFQs</Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myBids.slice(0, 6).map((bid) => (
                    <Card key={bid.id} className="card-base">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-500">RFQ Bid</span>
                          {getStatusBadge(bid.status)}
                        </div>
                        <div className="text-lg font-bold text-[#0D4F8B] mb-2">
                          {formatDualCurrency(bid.price_usd).usd}
                        </div>
                        <div className="text-xs text-slate-500 mb-2">
                          {formatDualCurrency(bid.price_usd).aed}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Clock className="w-3 h-3" />
                          {bid.lead_time_days} days lead time
                        </div>
                        {bid.status === 'pending' && (
                          <div className="mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditBidModal(bid)}
                            >
                              Edit
                            </Button>
                          </div>
                        )}
                        <div className="mt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openBargainModal(bid)}
                          >
                            Bargain
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <Dialog open={showEditRfqModal} onOpenChange={setShowEditRfqModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit RFQ</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateRfq} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Category *</label>
                <Select
                  value={editRfqForm.category}
                  onValueChange={(v) => setEditRfqForm({ ...editRfqForm, category: v })}
                >
                  <SelectTrigger aria-label="Edit RFQ category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Specifications *</label>
                <Textarea
                  value={editRfqForm.specs}
                  onChange={(e) => setEditRfqForm({ ...editRfqForm, specs: e.target.value })}
                  placeholder="Describe your product requirements..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Quantity *</label>
                  <Input
                    type="number"
                    value={editRfqForm.quantity}
                    onChange={(e) => setEditRfqForm({ ...editRfqForm, quantity: e.target.value })}
                    placeholder="Enter quantity"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Destination *</label>
                  <Select
                    value={editRfqForm.destination}
                    onValueChange={(v) => setEditRfqForm({ ...editRfqForm, destination: v })}
                  >
                    <SelectTrigger aria-label="Edit RFQ destination">
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {destinations.map((dest) => (
                        <SelectItem key={dest} value={dest}>{dest}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#10B981] hover:bg-[#059669]"
                disabled={!editRfqForm.category || !editRfqForm.specs || !editRfqForm.quantity || !editRfqForm.destination}
              >
                Save Changes
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showEditBidModal} onOpenChange={setShowEditBidModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Bid</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateBid} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Price (USD) *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editBidForm.price_usd}
                  onChange={(e) => setEditBidForm({ ...editBidForm, price_usd: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Lead Time (days) *</label>
                <Input
                  type="number"
                  value={editBidForm.lead_time_days}
                  onChange={(e) => setEditBidForm({ ...editBidForm, lead_time_days: e.target.value })}
                  placeholder="Days"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Message</label>
                <Textarea
                  value={editBidForm.message}
                  onChange={(e) => setEditBidForm({ ...editBidForm, message: e.target.value })}
                  placeholder="Additional details about your offer..."
                  rows={3}
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#10B981] hover:bg-[#059669]"
                disabled={!editBidForm.price_usd || !editBidForm.lead_time_days}
              >
                Save Changes
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showBargainModal} onOpenChange={setShowBargainModal}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Bargain</DialogTitle>
            </DialogHeader>
            {activeBargainBid && (
              <div className="space-y-4">
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Bid</span>
                    {getStatusBadge(activeBargainBid.status)}
                  </div>
                  <div className="mt-1 font-semibold text-[#0D4F8B]">
                    {formatDualCurrency(activeBargainBid.price_usd).usd}
                  </div>
                  <div className="text-xs text-slate-500">
                    Lead time: {activeBargainBid.lead_time_days} days
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-slate-700">Bargain History</h4>
                  {bargainHistory.length === 0 ? (
                    <p className="text-xs text-slate-500">No bargains yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {bargainHistory.map((entry, index) => {
                        const timestamp = formatBargainTimestamp(entry);
                        return (
                          <div key={entry.id || entry.created_at || index} className="rounded-md border border-slate-200 bg-white p-2">
                            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                              {entry.price_usd !== undefined && (
                                <>
                                  <span className="font-semibold text-slate-700">
                                    {formatDualCurrency(entry.price_usd).usd}
                                  </span>
                                  <span>{formatDualCurrency(entry.price_usd).aed}</span>
                                </>
                              )}
                              {entry.lead_time_days !== undefined && (
                                <span>{entry.lead_time_days} days</span>
                              )}
                              {timestamp && <span>{timestamp}</span>}
                            </div>
                            {entry.message && (
                              <p className="text-xs text-slate-600 mt-1">{entry.message}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-700">Send Bargain</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">
                        Price (USD) *
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={bargainForm.price_usd}
                        onChange={(e) => setBargainForm({ ...bargainForm, price_usd: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">
                        Lead time (days) *
                      </label>
                      <Input
                        type="number"
                        placeholder="Days"
                        value={bargainForm.lead_time_days}
                        onChange={(e) => setBargainForm({ ...bargainForm, lead_time_days: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">
                      Message
                    </label>
                    <Textarea
                      placeholder="Share your counter offer details..."
                      rows={3}
                      value={bargainForm.message}
                      onChange={(e) => setBargainForm({ ...bargainForm, message: e.target.value })}
                    />
                  </div>
                  <Button
                    size="sm"
                    className="bg-[#0D4F8B] hover:bg-[#0A3D6B]"
                    disabled={bargainLoading || !bargainForm.price_usd || !bargainForm.lead_time_days}
                    onClick={handleSubmitBargain}
                  >
                    Send Bargain
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Dashboard;

